import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, Wand2, RotateCcw, ImageIcon, MoveHorizontal, Download } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { fabrics, drapeStyles } from "../mock/mock";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { visualize } from "../lib/api";
import { resizeImage } from "../lib/image";

function BeforeAfter({ before, after }) {
  const [pos, setPos] = useState(50);
  const wrapRef = useRef(null);
  const dragging = useRef(false);

  const move = (clientX) => {
    const rect = wrapRef.current.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  };

  return (
    <div
      ref={wrapRef}
      className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl select-none cursor-ew-resize bg-black"
      onMouseMove={(e) => dragging.current && move(e.clientX)}
      onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
      onMouseUp={() => (dragging.current = false)}
      onMouseLeave={() => (dragging.current = false)}
      onTouchMove={(e) => move(e.touches[0].clientX)}
    >
      <img src={after} alt="After" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <span className="absolute top-3 right-3 z-20 px-3 py-1 rounded-full bg-[#A17E4A] text-white text-xs tracking-wide">AFTER</span>
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={before}
          alt="Before"
          className="absolute inset-0 h-full object-cover"
          style={{ width: wrapRef.current ? wrapRef.current.offsetWidth : "100%", maxWidth: "none" }}
          draggable={false}
        />
        <span className="absolute top-3 left-3 z-20 px-3 py-1 rounded-full bg-black/60 text-white text-xs tracking-wide">BEFORE</span>
      </div>
      <div className="absolute top-0 bottom-0 z-10" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-0.5 h-full bg-white" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center">
          <MoveHorizontal className="w-5 h-5 text-[#A17E4A]" />
        </div>
      </div>
    </div>
  );
}

export default function Visualizer() {
  const { toast } = useToast();
  const [image, setImage] = useState(null);
  const [fabric, setFabric] = useState(fabrics[0]);
  const [style, setStyle] = useState(drapeStyles[0]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => window.scrollTo({ top: 0 }), []);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Please upload an image file." });
      return;
    }
    try {
      const dataUrl = await resizeImage(file, 1280, 0.85);
      setImage(dataUrl);
      setResult(null);
    } catch {
      toast({ title: "Could not read that image. Try another photo." });
    }
  };

  const generate = async () => {
    if (!image) { toast({ title: "Upload a window photo first." }); return; }
    setLoading(true);
    setResult(null);
    try {
      const data = await visualize({ image, fabric: fabric.name, style: style.id });
      setResult(data.image);
      toast({ title: `Preview ready: ${fabric.name} \u2022 ${style.name}` });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Generation failed. Please try again.";
      toast({ title: msg });
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement("a");
    a.href = result;
    a.download = "godrapery-visualization.png";
    a.click();
  };

  return (
    <div className="bg-[#FBF8F3] min-h-screen">
      <Navbar />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-5 md:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-[#6B6259] hover:text-[#A17E4A] mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#A17E4A]/12 text-[#8a6a3c] text-xs tracking-widest uppercase mb-5">
            <Wand2 className="w-4 h-4" /> AI Drape Visualizer
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#2A2521] mb-4">
            See Before &amp; After
          </h1>
          <p className="text-[#6B6259] text-lg">
            Upload a photo of your window, choose a fabric and drapery style, and our AI renders a
            realistic preview of your transformed space.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Controls */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-xl text-[#2A2521] mb-4">1. Upload Window Photo</h3>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
                className="border-2 border-dashed border-[#D9CDB8] rounded-xl p-8 text-center cursor-pointer hover:border-[#A17E4A] hover:bg-[#FBF8F3] transition-colors"
              >
                {image ? (
                  <img src={image} alt="Your window" className="max-h-40 mx-auto rounded-lg" />
                ) : (
                  <div className="flex flex-col items-center gap-3 text-[#8A8177]">
                    <Upload className="w-9 h-9 text-[#A17E4A]" />
                    <p>Click or drag a photo here</p>
                    <p className="text-xs">JPG or PNG</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-xl text-[#2A2521] mb-4">2. Choose a Fabric</h3>
              <div className="grid grid-cols-3 gap-3">
                {fabrics.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFabric(f)}
                    className={`group text-left rounded-xl overflow-hidden border-2 transition-all ${
                      fabric.id === f.id ? "border-[#A17E4A] ring-2 ring-[#A17E4A]/25" : "border-transparent hover:border-[#D9CDB8]"
                    }`}
                  >
                    <div className="h-16 overflow-hidden">
                      <img src={f.swatch} alt={f.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    </div>
                    <span className="block px-2 py-1.5 text-xs text-[#4A423A] leading-tight">{f.name}</span>
                  </button>
                ))}
              </div>
              <p className="mt-3 text-sm text-[#8A8177]">{fabric.name} — {fabric.tone}</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-xl text-[#2A2521] mb-4">3. Drapery Style &amp; Heading</h3>
              <div className="grid grid-cols-2 gap-3">
                {drapeStyles.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setStyle(s)}
                    className={`text-left rounded-xl border-2 p-3 transition-all ${
                      style.id === s.id
                        ? "border-[#A17E4A] bg-[#FBF8F3] ring-2 ring-[#A17E4A]/20"
                        : "border-[#EAE3D8] hover:border-[#D9CDB8]"
                    }`}
                  >
                    <p className="font-medium text-[#2A2521] text-sm">{s.name}</p>
                    <p className="text-xs text-[#8A8177] mt-0.5">{s.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={generate}
              disabled={loading}
              className="w-full py-6 bg-[#A17E4A] hover:bg-[#8a6a3c] text-white rounded-full text-base disabled:opacity-70"
            >
              {loading ? (
                <><span className="w-4 h-4 mr-2 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Generating…</>
              ) : (
                <><Wand2 className="w-4 h-4 mr-2" /> Generate Before &amp; After</>
              )}
            </Button>
          </div>

          {/* Result */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-6 shadow-sm h-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-xl text-[#2A2521]">Result</h3>
                {result && (
                  <div className="flex items-center gap-4">
                    <button onClick={download} className="inline-flex items-center gap-2 text-sm text-[#8A8177] hover:text-[#A17E4A]">
                      <Download className="w-4 h-4" /> Save
                    </button>
                    <button onClick={() => { setResult(null); }} className="inline-flex items-center gap-2 text-sm text-[#8A8177] hover:text-[#A17E4A]">
                      <RotateCcw className="w-4 h-4" /> Reset
                    </button>
                  </div>
                )}
              </div>

              {loading ? (
                <div className="aspect-[4/3] rounded-2xl bg-[#F2ECE2] flex flex-col items-center justify-center text-[#8a6a3c] gap-4">
                  <span className="w-10 h-10 border-4 border-[#A17E4A]/30 border-t-[#A17E4A] rounded-full animate-spin" />
                  <p className="text-center px-6">Rendering your windows with<br /><span className="font-medium">{fabric.name} • {style.name}</span>…</p>
                  <p className="text-xs text-[#A9A093]">This can take 15–30 seconds</p>
                </div>
              ) : result ? (
                <div className="space-y-4">
                  <BeforeAfter before={image} after={result} />
                  <p className="text-sm text-[#8A8177] flex items-center gap-2">
                    <MoveHorizontal className="w-4 h-4" /> Drag the slider to compare — <span className="text-[#A17E4A] font-medium">{fabric.name}, {style.name}</span>
                  </p>
                </div>
              ) : (
                <div className="aspect-[4/3] rounded-2xl bg-[#F2ECE2] flex flex-col items-center justify-center text-[#A9A093] gap-3">
                  <ImageIcon className="w-12 h-12" />
                  <p>Your before &amp; after preview will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
